import { Roles } from '@omnea-digital/toolbox'
import { BlockTypeEnum } from '@omnea-app/core/blocks'
import { isSummaryApprovalBlock } from '@omnea-app/core/stages/isSummaryApprovalBlock'
import { RequestStepAction } from '@omnea-app/core/stages/utilEnums'
import {
  RequestStepResultEnum,
  RequestStepStateEnum,
  RequestStepTaskTypeEnum,
} from '@omnea-app/packages/universal/hasura/graphql'
import { BlockContext } from './types'
import type { RequestRecordPermissions } from '@omnea-digital/authz-model'

type ActionStepStates = RequestStepStateEnum | RequestStepResultEnum

const AllowedActions = [
  RequestStepAction.FORCE_APPROVE,
  RequestStepAction.REASSIGN_TO_SELF,
  RequestStepAction.HTTP_RETRY,
  RequestStepAction.REASSIGN_APPROVERS,
  RequestStepAction.ROLLBACK,
  RequestStepAction.SKIP,
  RequestStepAction.RERUN_ACCOUNTING_SYNC,
  RequestStepAction.UN_SKIP,
  RequestStepAction.POLL,
  RequestStepAction.REFRESH_STATUS,
  RequestStepAction.UPDATE_CONFIG,
  RequestStepAction.DATA_TRANSFORMATION_RETRY,
  RequestStepAction.SKIP_CONDITIONS,
  RequestStepAction.PROCEDURE_RETRY,
] as const satisfies RequestStepAction[]
type AllowedAction = (typeof AllowedActions)[number]

const ReassignmentActions: RequestStepAction[] = [
  RequestStepAction.REASSIGN_APPROVERS,
  RequestStepAction.REASSIGN_SUPPLIER,
  RequestStepAction.REASSIGN_ENGAGEMENT,
  RequestStepAction.REASSIGN_TO_SELF,
  RequestStepAction.REASSIGN_RENEWAL_ASSESSMENT,
  RequestStepAction.REASSIGN_EXTERNAL_OPERATION,
  RequestStepAction.REASSIGN_EXTERNAL_PROCEDURE,
  RequestStepAction.REASSIGN_PO_ASSIGNEES,
]

/** The record permission that decides who may take each step action on an FGA organisation. */
const STEP_ACTION_VERB = {
  [RequestStepAction.FORCE_APPROVE]: 'canSkip',
  [RequestStepAction.SKIP_CONDITIONS]: 'canAdminister',
  [RequestStepAction.SKIP]: 'canSkip',
  [RequestStepAction.UN_SKIP]: 'canSkip',
  [RequestStepAction.ROLLBACK]: 'canRollback',
  [RequestStepAction.REASSIGN_TO_SELF]: 'canManageAssignees',
  [RequestStepAction.REASSIGN_APPROVERS]: 'canManageAssignees',
  [RequestStepAction.POLL]: 'canManageIntegrations',
  [RequestStepAction.REFRESH_STATUS]: 'canManageIntegrations',
  [RequestStepAction.UPDATE_CONFIG]: 'canManageIntegrations',
  [RequestStepAction.HTTP_RETRY]: 'canManageIntegrations',
  [RequestStepAction.DATA_TRANSFORMATION_RETRY]: 'canManageIntegrations',
  [RequestStepAction.PROCEDURE_RETRY]: 'canManageIntegrations',
  [RequestStepAction.RERUN_ACCOUNTING_SYNC]: 'canManageIntegrations',
} as const satisfies Record<AllowedAction, keyof RequestRecordPermissions>

export type RequestStepVerbGates = Pick<RequestRecordPermissions, (typeof STEP_ACTION_VERB)[AllowedAction]>

const PausedActions = ReassignmentActions

export const skippableBlockTypes = [
  BlockTypeEnum.APPROVAL,
  BlockTypeEnum.SUPPLIER_ONBOARDING,
  BlockTypeEnum.CREATE_RENEWAL,
  BlockTypeEnum.COMMIT_SUPPLIER_DATA,
  BlockTypeEnum.CREATE_PO,
  BlockTypeEnum.RFX,
  BlockTypeEnum.CREATE_AGREEMENT,
  BlockTypeEnum.CANCEL_AGREEMENT,
  BlockTypeEnum.DATA_TRANSFORMATION,
  BlockTypeEnum.PRICE_BENCHMARK,
  BlockTypeEnum.EXTERNAL_OPERATION,
  BlockTypeEnum.EXTERNAL_PROCEDURE,
]

/** Block types that can be skipped when in a future (PENDING) state. Includes NOTIFICATION which only supports skip when pending, not running. */
export const futureSkippableBlockTypes = [...skippableBlockTypes, BlockTypeEnum.NOTIFICATION]

const rollbackableBlockTypes = [
  BlockTypeEnum.BUDGET_APPROVAL,
  BlockTypeEnum.APPROVAL,
  BlockTypeEnum.SUPPLIER_ONBOARDING,
  BlockTypeEnum.ENGAGEMENT,
  BlockTypeEnum.CREATE_RENEWAL,
  BlockTypeEnum.COMMIT_SUPPLIER_DATA,
  BlockTypeEnum.NOTIFICATION,
  BlockTypeEnum.CREATE_PO,
  BlockTypeEnum.CREATE_AGREEMENT,
  BlockTypeEnum.CREATE_ASSESSMENT,
  BlockTypeEnum.INTAKE,
  BlockTypeEnum.EXTERNAL_OPERATION,
  BlockTypeEnum.EXTERNAL_PROCEDURE,
  BlockTypeEnum.RENEWAL_ASSESSMENT,
  BlockTypeEnum.HTTP,
  BlockTypeEnum.MILESTONE,
]

export type FlagId = 'isAssignee'

interface PermissionRule {
  states: ActionStepStates[]
  role: Roles
  types: BlockTypeEnum[]
  // Only allow for the most recent stage of a request, if complete this is the final stage
  shouldOnlyApplyToMostRecentStage?: boolean
  omneanOnly?: boolean
  needsConfigPermission?: boolean
  mustNotBeViewer?: boolean
  mustBeAssignee?: boolean
  requiresAccountingIntegration?: boolean
  /**
   * The block has somewhere to commit to — a native accounting integration, or a custom one with a
   * procedure bound for this block's commit task. Wider than `requiresAccountingIntegration`, which
   * keeps meaning "the native accounting service is connected".
   */
  requiresAccountingCommitTarget?: boolean
  requiresTaskErrors?: boolean
  requiresAllUserTasksComplete?: boolean
  requiresStepNotLoading?: boolean
  checkFeatureFlag?: boolean
  killedByAssigneeRestriction?: boolean
  requiresOpenAmendment?: boolean
  requiresRequestInFlight?: boolean
  requiresConditionality?: boolean
  requiresFailedProcedureRun?: boolean
  requiresPurchaseOrderSyncTask?: boolean
}

type Permissions = Record<AllowedAction, PermissionRule[]>

const baseReassignmentPermissionRulesCore: PermissionRule[] = [
  // Note(Diego): In the future we will remove result: Error for updates and replace this with Stage.insertTaskError. More info at: https://github.com/omnea-digital/omnea-app/pull/10345
  {
    states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.PENDING, RequestStepStateEnum.PAUSED],
    types: [
      BlockTypeEnum.APPROVAL,
      BlockTypeEnum.SUPPLIER_ONBOARDING,
      BlockTypeEnum.RENEWAL_ASSESSMENT,
      BlockTypeEnum.ENGAGEMENT,
      BlockTypeEnum.CREATE_RENEWAL,
      BlockTypeEnum.COMMIT_SUPPLIER_DATA,
      BlockTypeEnum.CREATE_PO,
      BlockTypeEnum.EXTERNAL_OPERATION, // TODO EXP-1373 only if there is a task.
      BlockTypeEnum.EXTERNAL_PROCEDURE,
      BlockTypeEnum.CREATE_AGREEMENT,
      BlockTypeEnum.CANCEL_AGREEMENT,
      BlockTypeEnum.CREATE_ASSESSMENT,
      BlockTypeEnum.CANCEL_ASSESSMENTS,
      BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES,
    ],
    role: Roles.ADMIN,
    mustNotBeViewer: true,
  },
  {
    states: [RequestStepStateEnum.RUNNING],
    types: [BlockTypeEnum.DATA_TRANSFORMATION],
    role: Roles.ADMIN,
    mustNotBeViewer: true,
  },
  {
    states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.PAUSED],
    types: [BlockTypeEnum.BUDGET_APPROVAL],
    role: Roles.ADMIN,
    mustNotBeViewer: true,
  },
  {
    states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.PENDING, RequestStepStateEnum.PAUSED],
    types: [
      BlockTypeEnum.APPROVAL,
      BlockTypeEnum.SUPPLIER_ONBOARDING,
      BlockTypeEnum.RENEWAL_ASSESSMENT,
      BlockTypeEnum.ENGAGEMENT,
      BlockTypeEnum.CREATE_RENEWAL,
      BlockTypeEnum.COMMIT_SUPPLIER_DATA,
      BlockTypeEnum.CREATE_PO,
      BlockTypeEnum.EXTERNAL_OPERATION, // TODO EXP-1373 only if there is a task.
      BlockTypeEnum.EXTERNAL_PROCEDURE,
      BlockTypeEnum.CREATE_AGREEMENT,
      BlockTypeEnum.CANCEL_AGREEMENT,
      BlockTypeEnum.CREATE_ASSESSMENT,
      BlockTypeEnum.CANCEL_ASSESSMENTS,
      BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES,
    ],
    role: Roles.ADMIN,
    mustNotBeViewer: false,
    mustBeAssignee: true,
  },
  {
    states: [RequestStepStateEnum.RUNNING],
    types: [BlockTypeEnum.DATA_TRANSFORMATION],
    role: Roles.ADMIN,
    mustNotBeViewer: false,
    mustBeAssignee: true,
  },
  {
    states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.PAUSED],
    types: [BlockTypeEnum.BUDGET_APPROVAL],
    role: Roles.ADMIN,
    mustNotBeViewer: false,
    mustBeAssignee: true,
  },
  {
    states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.PENDING, RequestStepStateEnum.PAUSED],
    types: [
      BlockTypeEnum.APPROVAL,
      BlockTypeEnum.SUPPLIER_ONBOARDING,
      BlockTypeEnum.RENEWAL_ASSESSMENT,
      BlockTypeEnum.ENGAGEMENT,
    ],
    role: Roles.USER,
    mustBeAssignee: true,
    killedByAssigneeRestriction: true,
  },
  {
    states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.PAUSED],
    types: [BlockTypeEnum.BUDGET_APPROVAL],
    role: Roles.USER,
    mustBeAssignee: true,
    killedByAssigneeRestriction: true,
  },
]

// A step can stay RUNNING/PENDING/PAUSED after its parent request is rejected/cancelled, where the
// backend refuses the write. amendmentReopenedReassignmentRules below inherits this via its spread
// over these same rules.
const baseReassignmentPermissionRules: PermissionRule[] = baseReassignmentPermissionRulesCore.map((rule) => ({
  ...rule,
  requiresRequestInFlight: true,
}))

// An outstanding amendment effectively reopens a COMPLETE step.
const amendmentReopenedReassignmentRules: PermissionRule[] = baseReassignmentPermissionRules.map((rule) => ({
  ...rule,
  states: [RequestStepStateEnum.COMPLETE],
  requiresOpenAmendment: true,
}))

// A completed SUPPLIER_ONBOARDING step on a still-active request stays editable
// so admins can add new collaborators to the supplier portal.
const inFlightSupplierPortalReassignmentRules: PermissionRule[] = [
  {
    states: [RequestStepStateEnum.COMPLETE],
    types: [BlockTypeEnum.SUPPLIER_ONBOARDING],
    role: Roles.ADMIN,
    mustNotBeViewer: true,
    requiresRequestInFlight: true,
  },
  {
    states: [RequestStepStateEnum.COMPLETE],
    types: [BlockTypeEnum.SUPPLIER_ONBOARDING],
    role: Roles.ADMIN,
    mustBeAssignee: true,
    requiresRequestInFlight: true,
  },
  {
    states: [RequestStepStateEnum.COMPLETE],
    types: [BlockTypeEnum.SUPPLIER_ONBOARDING],
    role: Roles.USER,
    mustBeAssignee: true,
    killedByAssigneeRestriction: true,
    requiresRequestInFlight: true,
  },
]

const reassignmentPermissionRules: PermissionRule[] = [
  ...baseReassignmentPermissionRules,
  ...amendmentReopenedReassignmentRules,
  ...inFlightSupplierPortalReassignmentRules,
]

const permissions: Permissions = {
  [RequestStepAction.REASSIGN_APPROVERS]: reassignmentPermissionRules,
  [RequestStepAction.REASSIGN_TO_SELF]: reassignmentPermissionRules,

  [RequestStepAction.FORCE_APPROVE]: [
    {
      states: [RequestStepStateEnum.RUNNING],
      types: [BlockTypeEnum.BUDGET_APPROVAL, BlockTypeEnum.APPROVAL],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
  ],
  [RequestStepAction.SKIP]: [
    {
      states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.PENDING],
      types: skippableBlockTypes,
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
    {
      states: [RequestStepStateEnum.PENDING],
      types: [BlockTypeEnum.NOTIFICATION],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
    {
      states: [RequestStepStateEnum.RUNNING],
      types: [
        BlockTypeEnum.BUDGET_APPROVAL,
        BlockTypeEnum.EXTERNAL_OPERATION,
        BlockTypeEnum.CREATE_ASSESSMENT,
        BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES,
      ],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
    {
      states: [RequestStepResultEnum.ERROR],
      types: [
        BlockTypeEnum.HTTP,
        BlockTypeEnum.EXTERNAL_OPERATION,
        BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES,
        BlockTypeEnum.NOTIFY_SLACK,
      ],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
    {
      states: [RequestStepStateEnum.RUNNING],
      types: [BlockTypeEnum.ENGAGEMENT],
      omneanOnly: true,
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
  ],
  [RequestStepAction.UN_SKIP]: [
    {
      states: [RequestStepStateEnum.PENDING],
      types: skippableBlockTypes,
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
    {
      states: [RequestStepStateEnum.PENDING],
      types: [BlockTypeEnum.NOTIFICATION],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
    { states: [RequestStepResultEnum.ERROR], types: [BlockTypeEnum.HTTP], role: Roles.ADMIN, mustNotBeViewer: true },
  ],
  [RequestStepAction.ROLLBACK]: [
    {
      states: [RequestStepStateEnum.COMPLETE],
      types: rollbackableBlockTypes,
      omneanOnly: true,
      role: Roles.ADMIN,
      shouldOnlyApplyToMostRecentStage: false,
      mustNotBeViewer: true,
    },
    {
      states: [RequestStepStateEnum.COMPLETE],
      types: rollbackableBlockTypes,
      role: Roles.ADMIN,
      shouldOnlyApplyToMostRecentStage: false,
      mustNotBeViewer: true,
      needsConfigPermission: true,
    },
    {
      // Grant rollback to admins without config permission when enabled. featureFlaggedActions[ROLLBACK]
      // mirrors the `nonConfigAdminRollbackEnabled` org setting OR the legacy P2P_ADMIN_MODE flag, and the
      // backend already permits the admin role to roll back.
      states: [RequestStepStateEnum.COMPLETE],
      types: rollbackableBlockTypes,
      role: Roles.ADMIN,
      shouldOnlyApplyToMostRecentStage: false,
      mustNotBeViewer: true,
      checkFeatureFlag: true,
    },
  ],
  [RequestStepAction.RERUN_ACCOUNTING_SYNC]: [
    {
      // Engagement syncs to the native accounting service only, so it keeps the narrower gate.
      states: [RequestStepStateEnum.RUNNING],
      types: [BlockTypeEnum.ENGAGEMENT],
      role: Roles.ADMIN,
      requiresAccountingIntegration: true,
      requiresTaskErrors: true,
      requiresAllUserTasksComplete: true,
      requiresStepNotLoading: true,
      mustNotBeViewer: true,
    },
    {
      // The commit blocks also run on a custom ERP, where ExternalCommitClientFactory falls back to
      // the bound ExtOps procedure — so a custom-ERP org must be offered the rerun too.
      states: [RequestStepStateEnum.RUNNING],
      types: [BlockTypeEnum.COMMIT_SUPPLIER_DATA, BlockTypeEnum.CREATE_PO],
      role: Roles.ADMIN,
      requiresAccountingCommitTarget: true,
      requiresTaskErrors: true,
      requiresAllUserTasksComplete: true,
      requiresStepNotLoading: true,
      mustNotBeViewer: true,
    },
  ],
  [RequestStepAction.HTTP_RETRY]: [
    {
      states: [RequestStepResultEnum.ERROR, RequestStepStateEnum.RUNNING],
      types: [BlockTypeEnum.HTTP, BlockTypeEnum.EXTERNAL_OPERATION],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
      requiresTaskErrors: true,
    },
  ],
  [RequestStepAction.PROCEDURE_RETRY]: [
    {
      // Task errors alone would over-offer this: not every step error comes from a retryable run.
      states: [RequestStepStateEnum.RUNNING],
      types: [BlockTypeEnum.EXTERNAL_PROCEDURE],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
      requiresTaskErrors: true,
      requiresFailedProcedureRun: true,
    },
  ],
  [RequestStepAction.DATA_TRANSFORMATION_RETRY]: [
    {
      states: [RequestStepResultEnum.ERROR],
      types: [BlockTypeEnum.DATA_TRANSFORMATION],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
  ],
  [RequestStepAction.POLL]: [
    {
      states: [RequestStepStateEnum.RUNNING],
      types: [BlockTypeEnum.EXTERNAL_OPERATION],
      role: Roles.USER,
    },
    {
      states: [RequestStepStateEnum.RUNNING],
      types: [BlockTypeEnum.EXTERNAL_OPERATION],
      role: Roles.ADMIN,
    },
    {
      states: [RequestStepStateEnum.COMPLETE],
      types: [BlockTypeEnum.CREATE_PO],
      role: Roles.USER,
      requiresAccountingIntegration: true,
      requiresPurchaseOrderSyncTask: true,
    },
    {
      states: [RequestStepStateEnum.COMPLETE],
      types: [BlockTypeEnum.CREATE_PO],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
      requiresAccountingIntegration: true,
      requiresPurchaseOrderSyncTask: true,
    },
  ],
  [RequestStepAction.REFRESH_STATUS]: [
    {
      states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.COMPLETE],
      types: [BlockTypeEnum.EXTERNAL_OPERATION],
      role: Roles.USER,
    },
    {
      states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.COMPLETE],
      types: [BlockTypeEnum.EXTERNAL_OPERATION],
      role: Roles.ADMIN,
    },
  ],
  [RequestStepAction.SKIP_CONDITIONS]: [
    {
      states: [RequestStepStateEnum.PENDING],
      types: [
        BlockTypeEnum.APPROVAL,
        BlockTypeEnum.BUDGET_APPROVAL,
        BlockTypeEnum.ENGAGEMENT,
        BlockTypeEnum.HTTP,
        BlockTypeEnum.NOTIFICATION,
        BlockTypeEnum.RENEWAL_ASSESSMENT,
        BlockTypeEnum.SUPPLIER_ONBOARDING,
        BlockTypeEnum.EXTERNAL_OPERATION,
        BlockTypeEnum.EXTERNAL_PROCEDURE,
        BlockTypeEnum.CREATE_RENEWAL,
        BlockTypeEnum.COMMIT_SUPPLIER_DATA,
        BlockTypeEnum.CREATE_AGREEMENT,
        BlockTypeEnum.CANCEL_AGREEMENT,
        BlockTypeEnum.CREATE_PO,
        BlockTypeEnum.RFX,
        BlockTypeEnum.CREATE_ASSESSMENT,
        BlockTypeEnum.CANCEL_ASSESSMENTS,
        BlockTypeEnum.PRICE_BENCHMARK,
        BlockTypeEnum.DATA_TRANSFORMATION,
        BlockTypeEnum.WAITING,
        BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES,
      ],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
      needsConfigPermission: true,
      checkFeatureFlag: true,
      requiresConditionality: true,
    },
  ],
  [RequestStepAction.UPDATE_CONFIG]: [
    {
      states: [RequestStepStateEnum.PENDING],
      types: [
        BlockTypeEnum.EXTERNAL_OPERATION,
        BlockTypeEnum.CREATE_AGREEMENT,
        BlockTypeEnum.COMMIT_SUPPLIER_DATA,
        BlockTypeEnum.WAITING,
      ],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
      needsConfigPermission: true,
      checkFeatureFlag: true,
    },
    {
      states: [RequestStepResultEnum.ERROR, RequestStepStateEnum.RUNNING],
      types: [
        BlockTypeEnum.EXTERNAL_OPERATION,
        BlockTypeEnum.CREATE_AGREEMENT,
        BlockTypeEnum.COMMIT_SUPPLIER_DATA,
        BlockTypeEnum.WAITING,
      ],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
      needsConfigPermission: true,
      checkFeatureFlag: true,
    },
  ],
}

const isValidAction = (action: string): action is AllowedAction =>
  AllowedActions.some((allowedAction) => allowedAction === action)

type MatchPermissionSetProps = {
  rules: PermissionRule[]
  /**
   * The record verb governing this action, when the organisation is on the permissions service and
   * a verb governs it. It replaces the actor half of each rule — role, viewer, config-permission
   * and the assignee kill-switch — while `mustBeAssignee` rules keep working for anyone assigned
   * to the step, whatever verbs they hold. Undefined keeps the legacy role semantics.
   */
  verb: boolean | undefined
  role: Roles
  type: BlockTypeEnum
  state: ActionStepStates
  isAssignee: boolean
  isOmnean: boolean
  hasConfigPermission: boolean
  isViewOnly: boolean
  isMostRecentStage: boolean
  hasAccountingIntegration: boolean
  hasAccountingCommitTarget: boolean
  hasTaskErrors: boolean
  areAllUserTasksComplete: boolean
  isStepLoading: boolean
  featureFlagEnabled: boolean
  isAssigneeManagementRestricted: boolean
  hasOpenAmendment: boolean
  requestIsInFlight: boolean
  hasConditionality: boolean
  hasFailedProcedureRun: boolean
  hasPurchaseOrderSyncTask: boolean
}

// Both fields: a failure can be recorded before `execute` persists the run id the retry would name.
const stepHasFailedRun = (data: unknown): boolean =>
  typeof data === 'object' &&
  data !== null &&
  'runFailedAt' in data &&
  !!data.runFailedAt &&
  'procedureRunId' in data &&
  !!data.procedureRunId

const matchPermissionSet = ({
  rules,
  verb,
  role,
  type,
  state,
  isAssignee,
  isOmnean,
  hasConfigPermission,
  isViewOnly,
  isMostRecentStage,
  hasAccountingIntegration,
  hasAccountingCommitTarget,
  hasTaskErrors,
  areAllUserTasksComplete,
  isStepLoading,
  featureFlagEnabled,
  isAssigneeManagementRestricted,
  hasOpenAmendment,
  requestIsInFlight,
  hasConditionality,
  hasFailedProcedureRun,
  hasPurchaseOrderSyncTask,
}: MatchPermissionSetProps) => {
  return rules.some((rule) => {
    const actorAllowed =
      verb == null
        ? rule.role === role &&
          (rule.needsConfigPermission ? hasConfigPermission : true) &&
          (rule.mustNotBeViewer ? !isViewOnly : true) &&
          (rule.mustBeAssignee ? isAssignee : true) &&
          (rule.killedByAssigneeRestriction ? !isAssigneeManagementRestricted : true)
        : rule.mustBeAssignee
          ? isAssignee
          : verb
    return (
      actorAllowed &&
      rule.types.includes(type) &&
      rule.states.includes(state) &&
      (rule.omneanOnly ? isOmnean : true) &&
      (rule.shouldOnlyApplyToMostRecentStage ? isMostRecentStage : true) &&
      (rule.requiresAccountingIntegration ? hasAccountingIntegration : true) &&
      (rule.requiresAccountingCommitTarget ? hasAccountingCommitTarget : true) &&
      (rule.requiresTaskErrors ? hasTaskErrors : true) &&
      (rule.requiresAllUserTasksComplete ? areAllUserTasksComplete : true) &&
      (rule.requiresStepNotLoading ? !isStepLoading : true) &&
      (rule.checkFeatureFlag ? featureFlagEnabled : true) &&
      (rule.requiresOpenAmendment ? hasOpenAmendment : true) &&
      (rule.requiresRequestInFlight ? requestIsInFlight : true) &&
      (rule.requiresConditionality ? hasConditionality : true) &&
      (rule.requiresFailedProcedureRun ? hasFailedProcedureRun : true) &&
      (rule.requiresPurchaseOrderSyncTask ? hasPurchaseOrderSyncTask : true)
    )
  })
}

/** A flag may depend on the block type, so one action can be released per block type. */
type FeatureFlagGate = boolean | ((blockType: BlockTypeEnum) => boolean)
export type FeatureFlaggedActions = Partial<Record<RequestStepAction, FeatureFlagGate>>

const isFeatureFlagEnabled = (gate: FeatureFlagGate | undefined, blockType: BlockTypeEnum): boolean =>
  typeof gate === 'function' ? gate(blockType) : (gate ?? false)

interface GetAllowedActionsProps {
  context: BlockContext
  isAssignee: boolean
  hasAccountingIntegration: boolean
  /** Whether this block's commit task has a target, native or custom ERP. */
  hasAccountingCommitTarget?: boolean
  hasTaskErrors: boolean
  areAllUserTasksComplete: boolean
  isRequestPaused?: boolean
  isStepLoading: boolean
  featureFlaggedActions?: FeatureFlaggedActions
  restrictAssigneeManagement?: boolean
  hasOpenAmendment?: boolean
  requestIsInFlight?: boolean
  /** P2P_ADMIN_MODE: when on, admins without config permission lose force-approve/skip. Frontend-only HubSpot stop-gap. */
  p2pAdminMode?: boolean
  /**
   * Present when the organisation is on the permissions service: the verbs replace the role rules
   * (and the P2P clip and assignee kill-switch) for the actions they govern, while step-assignee
   * rules keep working for anyone assigned. Undefined keeps the legacy role semantics throughout.
   */
  verbGates?: RequestStepVerbGates
}
export const getAllowedActions = ({
  context,
  isAssignee,
  hasAccountingIntegration,
  hasAccountingCommitTarget = hasAccountingIntegration,
  hasTaskErrors,
  areAllUserTasksComplete,
  isRequestPaused,
  isStepLoading,
  featureFlaggedActions = {},
  restrictAssigneeManagement,
  hasOpenAmendment = false,
  requestIsInFlight = false,
  p2pAdminMode = false,
  verbGates,
}: GetAllowedActionsProps): RequestStepAction[] => {
  const { user, state, step, stepIsSkippedInFuture, type, isMostRecentStage, node } = context

  // A step is cancelled when its sub-request is halted (e.g. at RFx award); no further action should
  // be possible on it, so guard here before the reassign/skip rules below can offer any.
  if (step?.state === RequestStepStateEnum.CANCELLED) {
    return []
  }

  const isAssigneeManagementRestricted =
    !!restrictAssigneeManagement && user.role === Roles.USER && !user.hasConfigPermissions && !user.isViewOnly
  const hasConditionality = node?.metadata?.conditionConfig != null
  const result = step?.result ?? RequestStepResultEnum.PASS
  const actions: RequestStepAction[] = []

  // Special Case (consider converting to flag?)
  const actionState =
    result === RequestStepResultEnum.ERROR &&
    [
      BlockTypeEnum.HTTP,
      BlockTypeEnum.EXTERNAL_OPERATION,
      BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES,
      BlockTypeEnum.DATA_TRANSFORMATION,
      BlockTypeEnum.NOTIFY_SLACK,
    ].includes(type)
      ? result
      : state

  const hasPurchaseOrderSyncTask =
    step?.requestStepTasks.some((task) => task.type === RequestStepTaskTypeEnum.EXTERNAL_PO_CREATION) ?? false

  const filteredPermissions = Object.fromEntries(
    Object.entries(permissions).filter(
      ([action]) =>
        isValidAction(action) &&
        (stepIsSkippedInFuture ? action !== RequestStepAction.SKIP : action !== RequestStepAction.UN_SKIP) &&
        (isRequestPaused ? PausedActions.includes(action) : true)
    )
  )

  for (const [action, rules] of Object.entries(filteredPermissions)) {
    if (
      isValidAction(action) &&
      matchPermissionSet({
        rules,
        verb: verbGates?.[STEP_ACTION_VERB[action]],
        role: user.role,
        type,
        state: actionState,
        isAssignee,
        isOmnean: user.isOmnean,
        hasConfigPermission: user.hasConfigPermissions,
        // TODO SB(SR-504): Remove enforced isViewOnly
        isViewOnly: !!user.isViewOnly,
        isMostRecentStage,
        hasAccountingIntegration,
        hasAccountingCommitTarget,
        hasTaskErrors,
        areAllUserTasksComplete,
        isStepLoading,
        featureFlagEnabled: isFeatureFlagEnabled(featureFlaggedActions[action], type),
        isAssigneeManagementRestricted,
        hasOpenAmendment,
        requestIsInFlight,
        hasConditionality,
        hasFailedProcedureRun: stepHasFailedRun(step?.data),
        hasPurchaseOrderSyncTask,
      })
    ) {
      actions.push(action)
    }
  }

  // P2P_ADMIN_MODE (frontend-only HubSpot stop-gap): admins without config permission cannot
  // force-approve or skip request steps, and the P2P role specifically cannot reassign on quick
  // (summary) approvals. Super admins (hasConfigPermissions) are unaffected; regular users keep
  // reassignment on their assigned approval tasks. Superseded by the verbs on an FGA organisation.
  if (verbGates == null && p2pAdminMode && !user.hasConfigPermissions) {
    const payload = node?.metadata?.config?.payload
    const isQuickApproval = !!payload && 'tasks' in payload && isSummaryApprovalBlock(payload.tasks)
    const blockReassignment = isQuickApproval && user.role === Roles.ADMIN && !user.isViewOnly
    return actions.filter(
      (action) =>
        action !== RequestStepAction.FORCE_APPROVE &&
        action !== RequestStepAction.SKIP &&
        !(blockReassignment && ReassignmentActions.includes(action))
    )
  }

  return actions
}
